// src/pages/Login.jsx
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { apiFetch } from "../lib/api"; // still here in case you use later
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

export default function Login() {
  const seo = seoPresets["/login"];
  const { login, googleLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const redirect = params.get("redirect") || "/";

  async function onGoogleSuccess(cred) {
    try {
      setError("");
      await googleLogin(cred.credential);
      nav(redirect);
    } catch (e) {
      setError(e?.message || "Google sign-in failed");
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav(redirect);
    } catch (err) {
      setError(err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
    <div className="auth-wrap">
      <style>{css}</style>

      <div className="auth-grid">
        {/* LEFT: login */}
        <section className="auth-main">
          <header className="auth-header">
            <Link to="/" className="brand-link">
              <div className="logo"><img
                className="brand-icon"
                src="https://imgur.com/ef4tlNy.png"
                alt=""
                aria-hidden
              /></div>
              <div className="brand-names">
                <span className="brand-top">MusicBase</span>
                <span className="brand-sub">playlist outreach</span>
              </div>
            </Link>

            <div className="auth-switch">
              <button type="button" className="auth-switch-btn active">
                Log in
              </button>
              <button
                type="button"
                className="auth-switch-btn"
                onClick={() => nav("/signup")}
              >
                Sign up
              </button>
            </div>
          </header>

          <div className="auth-body">
            <h1 className="auth-title">Welcome back</h1>
            <p className="auth-subtitle">
              Log in to search playlists, build outreach campaigns, and track
              curator replies from one place.
            </p>

            {/* Google login */}
            <div className="auth-social">
              <div className="auth-social-inner">
                <GoogleOAuthProvider
                  clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}
                >
                  <GoogleLogin
                    text="continue_with"
                    onSuccess={onGoogleSuccess}
                    onError={() => setError("Google sign-in failed")}
                  />
                </GoogleOAuthProvider>
              </div>
            </div>

            <div className="auth-divider">
              <span />
              <span>or continue through</span>
              <span />
            </div>

            <form onSubmit={onSubmit} className="form">
              <div className={"field" + (email ? " has-value" : "")}>
                <input
                  id="email"
                  type="email"
                  placeholder=" "
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <label htmlFor="email">Email</label>
              </div>

              <div className={"field" + (password ? " has-value" : "")}>
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  placeholder=" "
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <label htmlFor="password">Password</label>
                <button
                  type="button"
                  className="showpw"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? "🙈" : "👁️"}
                </button>
              </div>

              <div className="auth-row">
                <label className="remember">
                  <input type="checkbox" defaultChecked />
                  <span>Keep me signed in</span>
                </label>
                <Link to="/forgot-password" className="link-small">
                  Forgot password?
                </Link>
              </div>

              {error && <div className="error">{error}</div>}

              <button className="submit" type="submit" disabled={loading}>
                {loading ? "Signing you in..." : "Log in"}
              </button>
            </form>

            <p className="auth-footnote">
              By continuing, you agree to MusicBase&apos;s{" "}
              <Link to="/terms">Terms</Link> and{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>

            {/* no extra “Don’t have an account?” here – it’s in the top toggle */}
          </div>
        </section>

        {/* RIGHT SIDE — Apollo-style hero, but realistic metrics */}
        <aside className="auth-aside" aria-hidden="true">
          <div className="hero-shell">
            <div className="hero-pill">
              <span className="hero-dot" />
              <span>Campaign preview</span>
            </div>

            <h2 className="hero-title">
              See delivered and replies in one view.
            </h2>
            <p className="hero-text">
              As your campaigns run, MusicBase shows how many emails were
              delivered, bounced, or got a reply — the same metrics you see in
              your Campaigns table.
            </p>

            {/* Screenshot-style card that mirrors Campaign stats */}
            <div className="hero-card">
              <div className="hero-card-header">
                <div className="hero-avatar">MB</div>
                <div className="hero-card-text">
                  <div className="hero-card-title">Indie Pitch — Sample</div>
                  <div className="hero-card-sub">
                    Campaign overview · preview only
                  </div>
                </div>
              </div>

              <div className="hero-rows">
                <div className="hero-row">
                  <span className="hero-label">Delivered</span>
                  <span className="hero-value">54</span>
                </div>
                <div className="hero-row">
                  <span className="hero-label">Replied</span>
                  <span className="hero-value">29</span>
                </div>
                <div className="hero-row">
                  <span className="hero-label">Bounced</span>
                  <span className="hero-value">2</span>
                </div>
                <div className="hero-row">
                  <span className="hero-label">Spam</span>
                  <span className="hero-value">1</span>
                </div>
              </div>
            </div>

            {/* Neutral feature bullets instead of fake “800,000+” users */}
            <div className="hero-metrics">
              <div>
                <div className="hero-metric-number">Outreach stats</div>
                <div className="hero-metric-label">
                  Delivered, bounced and replied counts for every campaign.
                </div>
              </div>
              <div>
                <div className="hero-metric-number">Your own inbox</div>
                <div className="hero-metric-label">
                  Connect Gmail or Outlook so the data comes from your mailbox,
                  not guesses.
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
    </>
  );
}

const css = `
:root{
  --bg:#f5f4ff;
  --surface:#ffffff;
  --surface-soft:#f9fafb;
  --muted:#6b7280;
  --text:#0b0b0c;
  --accent:#7c5cff;
  --accent-2:#00d4ff;
  --ring:rgba(124,92,255,.25);
  --border:#e5e7eb;
  --shadow:0 24px 70px rgba(15,23,42,.14);
}

/* keep login page non-scrollable / centered */
html, body {
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  scroll-behavior: smooth;
}

*{ box-sizing:border-box; }

.auth-wrap{
  min-height:100dvh;
  background:
    radial-gradient(1100px 650px at -10% -10%, rgba(124,92,255,.18), transparent 55%),
    radial-gradient(1100px 650px at 110% 0%, rgba(0,212,255,.18), transparent 55%),
    linear-gradient(#ffffff, #f9fafb);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:32px 16px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.auth-grid{
  max-width:1120px;
  background:var(--surface);
  border-radius:24px;
  box-shadow:var(--shadow);
  border:1px solid var(--border);
  display:grid;
  grid-template-columns:minmax(0,440px) minmax(0,1fr);
  overflow:hidden;
}

/* LEFT SIDE */
.auth-main{
  padding:28px 32px 26px;
  display:flex;
  flex-direction:column;
  background:linear-gradient(180deg,#ffffff,#f9fafb);
}

.auth-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:16px;
  margin-bottom:24px;
}

.brand-link{
  display:flex;
  align-items:center;
  gap:12px;
  text-decoration:none;
  color:inherit;
}
.logo{
  width:40px; height:40px;
  border-radius:14px;
  display:grid; place-items:center;
  font-size:18px; font-weight:900; color:#fff;
}
.brand-names{
  display:flex;
  flex-direction:column;
  line-height:1.05;
  align-items:flex-start;
  text-align:left;
}.brand-top{font-weight:800; letter-spacing:.03em; font-size:15px;}
.brand-sub{font-size:11px; text-transform:uppercase; letter-spacing:.16em; color:var(--muted);}

/* toggle */
.auth-switch{
  display:inline-flex;
  padding:4px;
  border-radius:999px;
  background:#f3f4ff;
  border:1px solid #e0e7ff;
}
.auth-switch-btn{
  border:none;
  background:transparent;
  color:#6b7280;
  padding:6px 14px;
  border-radius:999px;
  font-size:12px;
  font-weight:600;
  cursor:pointer;
}
.auth-switch-btn.active{
  background:#111827;
  color:#f9fafb;
}

/* copy + form */
.auth-body{
  flex:1;
  display:flex;
  flex-direction:column;
}

.auth-title{
  margin:0 0 6px 0;
  font-size:30px;
  line-height:1.1;
  letter-spacing:-.02em;
  color:var(--text);
  text-align:center;
}
.auth-subtitle{
  margin:0 0 18px 0;
  font-size:14px;
  color:var(--muted);
  max-width:32rem;
  text-align:center;
}

/* Google */
.auth-social{
  margin-bottom:12px;
}
.auth-social-inner{
  border-radius:14px;
  padding:10px 10px;
  background:#ffffff;
  display:flex;
  justify-content:center;
}

/* divider */
.auth-divider{
  display:grid;
  grid-template-columns:1fr auto 1fr;
  align-items:center;
  gap:8px;
  margin:10px 0 14px;
}
.auth-divider span:nth-child(1),
.auth-divider span:nth-child(3){
  height:1px;
  background:#9ca3af;
}
.auth-divider span:nth-child(2){
  font-size:11px;
  font-weight:600;
  text-transform:uppercase;
  letter-spacing:.16em;
  color:#9ca3af;
}

/* fields */
.form{display:grid; gap:12px;}

.field{position:relative;}
.field input{
  width:100%;
  padding:16px 90px 10px 14px;
  border-radius:14px;
  border:1px solid var(--border);
  background:#ffffff;
  color:var(--text);
  font-size:14px;
  outline:none;
}
.field input::placeholder{color:transparent;}
.field label{
  position:absolute;
  left:14px;
  top:13px;
  pointer-events:none;
  color:var(--muted);
  background:#ffffff;
  padding:0 .25em;
  border-radius:999px;
  transition:all .16s ease;
  font-size:13px;
}
.field input:focus{
  box-shadow:0 0 0 5px var(--ring);
  border-color:#c4b5fd;
}
.field input:focus + label,
.field.has-value label{
  top:-8px;
  font-size:11px;
  color:#4b5563;
}

/* show / hide password */
.showpw{
  position:absolute;
  right:10px;
  top:9px;
  padding:6px 10px;
  font-size:12px;
  border-radius:999px;
  border:1px solid #e5e7eb;
  background:#f9fafb;
  cursor:pointer;
}
.showpw:hover{
  background:#eef2ff;
}

/* remember + forgot */
.auth-row{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  margin-top:2px;
}
.remember{
  display:inline-flex;
  align-items:center;
  gap:6px;
  font-size:12px;
  color:#6b7280;
}
.remember input{
  width:14px;
  height:14px;
}

.link-small{
  font-size:12px;
  color:#4b5563;
  text-decoration:none;
}
.link-small:hover{text-decoration:underline;}

/* error + submit */
.error{
  background:#fef2f2;
  border:1px solid #fecaca;
  color:#7f1d1d;
  padding:10px 12px;
  border-radius:12px;
  font-size:13px;
}

.submit{
  margin-top:4px;
  padding:13px 14px;
  border-radius:14px;
  font-weight:800;
  letter-spacing:.03em;
  border:none;
  cursor:pointer;
  background:linear-gradient(90deg,var(--nav-accent),var(--nav-accent-2));
  color:#fff;
  box-shadow:0 10px 40px rgba(124,92,255,.35);
}
.submit:disabled{
  opacity:.7;
  cursor:default;
  box-shadow:none;
}

.auth-footnote{
  margin:10px 0 0;
  font-size:11px;
  color:#9ca3af;
}
.auth-footnote a{
  color:#7c5cff;
  text-decoration:underline;
}

/* RIGHT SIDE */
.auth-aside{
  padding:32px 40px;
  background:linear-gradient(135deg,#eef2ff,#fdfbff);
  display:flex;
  align-items:center;
  justify-content:center;
}
.hero-shell{
  max-width:380px;
}
.hero-pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 10px;
  border-radius:999px;
  background:#111827;
  color:#e5e7eb;
  font-size:11px;
  font-weight:600;
  text-transform:uppercase;
  letter-spacing:.16em;
  margin-bottom:10px;
}
.hero-dot{
  width:7px; height:7px; border-radius:999px;
  background:linear-gradient(135deg,var(--accent),var(--accent-2));
}
.hero-title{
  margin:0 0 8px;
  font-size:24px;
  line-height:1.25;
  letter-spacing:-.02em;
  color:#020617;
}
.hero-text{
  margin:0 0 18px;
  font-size:14px;
  color:#4b5563;
}

/* screenshot-style card */
.hero-card{
  background:#ffffff;
  border-radius:18px;
  padding:16px 16px 14px;
  border:1px solid rgba(148,163,184,.55);
  box-shadow:0 22px 60px rgba(15,23,42,.16);
  margin-bottom:18px;
}
.hero-card-header{
  display:flex;
  align-items:center;
  gap:10px;
  margin-bottom:10px;
}
.hero-avatar{
  width:34px; height:34px;
  border-radius:12px;
  background:linear-gradient(90deg,var(--nav-accent),var(--nav-accent-2));
  display:grid; place-items:center;
  font-size:13px; font-weight:800; color:#fff;
}
.hero-card-text{
  display:flex;
  flex-direction:column;
}
.hero-card-title{
  font-size:14px;
  font-weight:600;
  color:#020617;
}
.hero-card-sub{
  font-size:12px;
  color:#6b7280;
}
.hero-rows{
  display:grid;
  gap:6px;
}
.hero-row{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:12px;
}
.hero-label{
  width:72px;
  color:#6b7280;
}
.hero-value{
  color:#111827;
}

/* metrics */
.hero-metrics{
  display:flex;
  flex-wrap:wrap;
  gap:18px;
}
.hero-metric-number{
  font-size:18px;
  font-weight:800;
  color:#020617;
}
.hero-metric-label{
  font-size:12px;
  color:#4b5563;
  max-width:220px;
}

@media (max-width: 960px){
  .auth-grid{
    border-radius:0px;
  }
  body {
    overflow-y: auto;}
  .auth-grid{
    grid-template-columns:1fr;
    max-width:560px;
  }
  .auth-aside{
    /* order:-1;  <-- remove this so signup stays first */
    padding:24px 24px 12px;
  }
  .auth-main{
    padding:24px 22px 24px;
  }
  .auth-copy{
    max-width:none;
  }
}

@media (max-width: 560px){
  .auth-wrap{padding:0px;}
  .auth-header{align-items:flex-start;}
  .auth-switch{align-self:flex-end;}
}
`;
